/**
 * Vincent B681 (Eric 2026-02-XX) — Custom hook `useTranscripts` : encapsule
 * les états et handlers de cross-validation + polling merged transcript.
 *
 * Vincent B685 (Eric 2026-02-XX) — Migration TanStack Query :
 *   - `pollMergedTranscript` invalide le cache ['transcripts', videoId] au
 *     lieu de faire des axios.get manuels + setAllTranscripts.
 *   - Résultat : les onglets Whisper/Fireflies/Merged voient tous la donnée
 *     à jour grâce au cache partagé.
 *
 * Signature inchangée pour ne pas casser VideoDetailPage.js.
 */
import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const fetchAllTranscripts = async ({ queryKey }) => {
  const [, videoId, api] = queryKey;
  const { data } = await axios.get(
    `${api}/api/videos/${videoId}/transcripts/all`,
    {
      withCredentials: true,
      // Vincent B1015 (Eric 2026-02-26 SPEC STRICK PROD) — Endpoint polled
      // sous react-query (staleTime + refetchInterval). Le retry storm
      // (8 tentatives × 90s) saturait le pool Chrome → ERR_INSUFFICIENT_
      // RESOURCES → banner « Reconnexion en cours » infini à 2 users.
      _maxRetries: 1,
      silent: true,
    }
  );
  return data;
};

export default function useTranscripts({ videoId, api, lang = "fr", setAllTranscripts, canEditKnowledge = false }) {
  const queryClient = useQueryClient();
  const [transcriptSubTab, setTranscriptSubTab] = useState(
    canEditKnowledge ? "whisper" : "fireflies"
  );
  const [crossValidateText, setCrossValidateText] = useState("");
  const [crossValidating, setCrossValidating] = useState(false);
  const [crossValidateResult, setCrossValidateResult] = useState(null);

  const pollMergedTranscript = useCallback(async () => {
    const MAX_ATTEMPTS = 36; // 36 * 5s = 3 minutes
    const queryKey = ["transcripts", videoId, api];
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        // Force refetch via queryClient (utilise le queryFn de useTranscriptGeneration)
        const data = await queryClient.fetchQuery({ queryKey, queryFn: fetchAllTranscripts, staleTime: 0 });
        if (data?.summary?.has_merged) {
          setCrossValidating(false);
          return;
        }
      } catch (e) {
        console.warn("poll error", e?.message);
      }
    }
    setCrossValidating(false);
    try {
      toast.error(
        lang === "fr"
          ? "Fusion plus longue qu'attendu (3 min). Cliquez sur 'Rafraichir' pour vérifier."
          : "Merge taking longer than expected (3 min). Click 'Refresh' to check."
      );
    } catch (e) {
      console.warn("[Merge] cross-validation timeout toast failed:", e);
    }
  }, [videoId, api, lang, queryClient]);

  return {
    transcriptSubTab,
    setTranscriptSubTab,
    crossValidateText,
    setCrossValidateText,
    crossValidating,
    setCrossValidating,
    crossValidateResult,
    setCrossValidateResult,
    pollMergedTranscript,
  };
}
